/**
 * Trend chart showing multiple KPI series over time.
 * Inline SVG — no external charting dependency.
 *
 * Each series is scaled to its own min/max range for readability.
 * QoQ / WoW toggle swaps the data series (quarter-level vs week-level).
 *
 * Colors: accent #D97757, success green #6BFFAB, info blue #6FA8FF.
 * Approach mirrors Sparkline.tsx — hand-rolled SVG with area fill under each line,
 * gridlines, axis labels, and a terminal dot per series.
 */
import { Box, Stack, Typography } from '@mui/material';
import { type ReactElement, useId, useState } from 'react';

import type { ScorecardGroup } from '@/types/scorecard';

interface TrendChartProps {
  groups: ScorecardGroup[];
}

type TrendMode = 'qoq' | 'wow';

// Highlight three key delivery metrics by default
const FEATURED_KEYS = ['eta_reliability', 'throughput', 'cycle_adherence'];
const SERIES_COLORS = ['#D97757', '#6BFFAB', '#6FA8FF'];

// Chart geometry constants
const W = 720;
const H = 200;
const PAD_L = 28;
const PAD_R = 18;
const PAD_T = 14;
const PAD_B = 34;

function normalizeToRange(arr: number[]): number[] {
  const mn = Math.min(...arr);
  const mx = Math.max(...arr);
  const rng = mx - mn;
  if (rng === 0) return arr.map(() => 50);
  return arr.map((v) => ((v - mn) / rng) * 100);
}

interface SeriesData {
  key: string;
  label: string;
  color: string;
  data: number[];
}

interface InlineTrendSvgProps {
  series: SeriesData[];
  xLabels: string[];
  idPrefix: string;
}

function InlineTrendSvg({ series, xLabels, idPrefix }: InlineTrendSvgProps): ReactElement {
  const n = Math.max(...series.map((s) => s.data.length), 2);
  const chartW = W - PAD_L - PAD_R;
  const chartH = H - PAD_T - PAD_B;

  // X positions for each label index
  const xPos = (i: number): number => PAD_L + (i / (n - 1)) * chartW;
  // Y position given a normalized 0–100 value
  const yPos = (v: number): number => PAD_T + chartH - (v / 100) * chartH;

  // Draw horizontal gridlines at 0, 25, 50, 75, 100 (normalized)
  const gridLines = [0, 25, 50, 75, 100].map((pct) => {
    const y = yPos(pct);
    return (
      <line
        key={pct}
        x1={PAD_L}
        y1={y}
        x2={W - PAD_R}
        y2={y}
        stroke="#25252e"
        strokeWidth={1}
        strokeDasharray="4 4"
      />
    );
  });

  // Draw vertical gridlines at each x label position
  const vLines = xLabels.map((_lab, i) => {
    if (i >= n) return null;
    const x = xPos(i);
    return (
      <line
        key={`vl-${i}`}
        x1={x}
        y1={PAD_T}
        x2={x}
        y2={H - PAD_B}
        stroke="#1f1f27"
        strokeWidth={1}
      />
    );
  });

  // Bottom axis label text
  const axisLabels = xLabels.map((lab, i) => {
    if (!lab || i >= n) return null;
    return (
      <text
        key={`ax-${i}`}
        x={xPos(i)}
        y={H - 10}
        fill="#6c6c78"
        fontFamily='"JetBrains Mono", monospace'
        fontSize={9}
        textAnchor="middle"
      >
        {lab}
      </text>
    );
  });

  // Render each series as area + line + terminal dot
  const seriesElements = series.map((s, idx) => {
    if (s.data.length < 2) return null;
    const len = s.data.length;
    const gradId = `${idPrefix}-g${idx}`;

    let linePath = '';
    let areaPath = '';
    s.data.forEach((v, i) => {
      const x = xPos(i).toFixed(1);
      const y = yPos(v).toFixed(1);
      linePath += `${i === 0 ? 'M' : 'L'}${x},${y} `;
      areaPath += `${i === 0 ? 'M' : 'L'}${x},${y} `;
    });
    // Close area path to bottom
    areaPath += `L${xPos(len - 1).toFixed(1)},${H - PAD_B} L${xPos(0).toFixed(1)},${H - PAD_B} Z`;

    // Terminal dot
    // reason: s.data.length >= 2 guaranteed by the early return above
    const lastVal = s.data[len - 1] as number;
    const lastX = xPos(len - 1);
    const lastY = yPos(lastVal);

    // Dots at each data point (for QoQ with small n; or every 3rd for WoW)
    const showAllDots = len <= 5;
    const dots = s.data.map((v, i) => {
      if (!showAllDots && i % 3 !== 0 && i !== len - 1) return null;
      return (
        <circle key={`dot-${idx}-${i}`} cx={xPos(i)} cy={yPos(v)} r={2.6} fill={s.color} />
      );
    });

    return (
      <g key={s.key}>
        <defs>
          <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor={s.color} stopOpacity={0.14} />
            <stop offset="1" stopColor={s.color} stopOpacity={0} />
          </linearGradient>
        </defs>
        <path d={areaPath} fill={`url(#${gradId})`} />
        <path
          d={linePath}
          fill="none"
          stroke={s.color}
          strokeWidth={2}
          strokeLinejoin="round"
          strokeLinecap="round"
        />
        {dots}
        <circle cx={lastX} cy={lastY} r={2.6} fill={s.color} />
      </g>
    );
  });

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      width="100%"
      height={H}
      aria-label="KPI trend chart"
      role="img"
      preserveAspectRatio="xMidYMid meet"
      style={{ display: 'block' }}
    >
      {/* Grid */}
      {gridLines}
      {vLines}
      {/* Axis labels */}
      {axisLabels}
      {/* Series */}
      {seriesElements}
    </svg>
  );
}

export function TrendChart({ groups }: TrendChartProps): ReactElement {
  const [mode, setMode] = useState<TrendMode>('qoq');
  const idPrefix = useId().replace(/:/g, '');

  // Flatten all metrics
  const allMetrics = groups.flatMap((g) => g.metrics);

  // Pick featured metrics that exist in data
  const featured = FEATURED_KEYS.map((k) => allMetrics.find((m) => m.key === k)).filter(
    Boolean,
  ) as NonNullable<ReturnType<typeof allMetrics.find>>[];

  // Build series from the spark data (weekly buckets).
  // For QoQ we take every 4th point (quarter-level); for WoW all 13 points.
  const seriesData: SeriesData[] = featured.map((m, idx) => {
    const raw = m.spark.length > 0 ? m.spark : [0];
    const sliced = mode === 'qoq' ? raw.filter((_, i) => i % 4 === 0) : raw;
    const normalized = normalizeToRange(sliced);
    return {
      key: m.key,
      label: m.label,
      color: SERIES_COLORS[idx] ?? '#D97757',
      data: normalized,
    };
  });

  // X-axis labels
  const maxLen = Math.max(...seriesData.map((s) => s.data.length), 2);
  const xLabels: string[] =
    mode === 'qoq'
      ? Array.from({ length: maxLen }, (_, i) => `Q${i + 1}`)
      : Array.from({ length: maxLen }, (_, i) =>
          i === 0 || i % 3 === 0 || i === maxLen - 1 ? `W${i + 1}` : '',
        );

  const hasData = seriesData.length > 0 && maxLen > 1;

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: '8px',
        bgcolor: 'background.paper',
        p: '17px 19px',
        mb: 2.75,
      }}
    >
      {/* Header */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        gap={1.5}
        sx={{ mb: 0.75 }}
      >
        <Typography
          sx={{
            fontFamily: '"JetBrains Mono", monospace',
            fontSize: '0.8125rem',
            fontWeight: 700,
            letterSpacing: '0.5px',
            textTransform: 'uppercase',
            color: 'text.primary',
          }}
        >
          Trend
        </Typography>

        {/* QoQ / WoW toggle */}
        <Box
          sx={{
            display: 'inline-flex',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: '6px',
            overflow: 'hidden',
            bgcolor: 'background.deeper',
          }}
          role="group"
          aria-label="Trend time granularity"
        >
          {([['qoq', 'Quarter / quarter'], ['wow', 'Week / week']] as const).map(
            ([key, label]) => (
              <Box
                key={key}
                component="button"
                onClick={() => setMode(key)}
                aria-pressed={mode === key}
                aria-label={label}
                sx={{
                  fontFamily: '"JetBrains Mono", monospace',
                  fontSize: '0.6875rem',
                  fontWeight: 600,
                  letterSpacing: '0.3px',
                  textTransform: 'uppercase',
                  px: 1.625,
                  minHeight: 44,
                  border: 'none',
                  cursor: 'pointer',
                  transition: 'all 140ms',
                  ...(mode === key
                    ? {
                        bgcolor: 'rgba(217,119,87,0.10)',
                        color: '#D97757',
                      }
                    : {
                        background: 'transparent',
                        color: 'text.disabled',
                        '&:hover': { color: 'text.primary' },
                      }),
                }}
              >
                {label}
              </Box>
            ),
          )}
        </Box>
      </Stack>

      {/* Chart */}
      <Box sx={{ width: '100%' }}>
        {hasData ? (
          <InlineTrendSvg series={seriesData} xLabels={xLabels} idPrefix={idPrefix} />
        ) : (
          <Box
            sx={{
              height: H,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'text.disabled',
            }}
          >
            <Typography
              sx={{
                fontFamily: '"JetBrains Mono", monospace',
                fontSize: '0.75rem',
              }}
            >
              No trend data available
            </Typography>
          </Box>
        )}
      </Box>

      {/* Legend */}
      <Stack direction="row" spacing={2.25} flexWrap="wrap" sx={{ mt: 1 }}>
        {seriesData.map(({ key, label, color }) => (
          <Stack key={key} direction="row" alignItems="center" spacing={0.875}>
            <Box
              sx={{ width: 14, height: 3, borderRadius: '2px', bgcolor: color, flexShrink: 0 }}
              aria-hidden="true"
            />
            <Typography
              sx={{
                fontFamily: '"JetBrains Mono", monospace',
                fontSize: '0.625rem',
                color: 'text.secondary',
              }}
            >
              {label}
            </Typography>
          </Stack>
        ))}
        <Typography
          sx={{
            fontFamily: '"JetBrains Mono", monospace',
            fontSize: '0.625rem',
            color: 'text.faintest',
            ml: 'auto',
          }}
        >
          Each series scaled to its own range
        </Typography>
      </Stack>
    </Box>
  );
}
